// src/components/study/StudySidebarPanel.tsx
// =====================================================================
// SIDEBAR SOB DEMANDA - IDENTIDADE GRAIA
// ✅ NOVO: Duas abas — Sessão Atual | Progresso
// =====================================================================

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { SubdeckProgressData } from '../../hooks/useSubdeckProgress';

interface StudySidebarPanelProps {
    isOpen: boolean;
    onClose: () => void;
    // Aba Sessão
    cardsStudied: number;
    correctAnswers: number;
    precision: number;
    ratings: {
        1: number;
        2: number;
        3: number;
        4: number;
        5: number;
    };
    // Aba Progresso (opcional — não aparece em modos sem subdeck fixo)
    progressData?: SubdeckProgressData | null;
    progressLoading?: boolean;
    onOpenProgressTab?: () => void;
}

// =====================================================================
// HELPER: formatar timestamp
// =====================================================================
function formatTimestamp(ts: number): string {
    const date = new Date(ts);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    if (isToday) return `hoje, ${time}`;
    if (isYesterday) return `ontem, ${time}`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) + `, ${time}`;
}

function formatDelta(delta: number, suffix = '%'): string {
    if (delta > 0) return `+${delta}${suffix}`;
    if (delta < 0) return `${delta}${suffix}`;
    return `estável`;
}

// =====================================================================
// ABA: SESSÃO ATUAL (sem alteração de conteúdo)
// =====================================================================
const SessionTab: React.FC<{
    cardsStudied: number;
    correctAnswers: number;
    precision: number;
    ratings: { 1: number; 2: number; 3: number; 4: number; 5: number };
}> = ({ cardsStudied, correctAnswers, precision, ratings }) => {
    const svgTrack = getComputedStyle(document.body).getPropertyValue('--platform-a12').trim();
    const svgFill = getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim();
    const getMotivationalMessage = () => {
        if (precision >= 80) return 'Consistência adequada';
        if (precision >= 60) return 'Ritmo estável';
        return 'Conteúdo em consolidação';
    };

    return (
        <div className="sidebar-content">
            <div className="stats-basic">
                <div className="stat-simple">
                    <span className="stat-simple-value">{cardsStudied}</span>
                    <span className="stat-simple-label">Cards estudados</span>
                </div>
                <div className="stat-simple">
                    <span className="stat-simple-value">{correctAnswers}</span>
                    <span className="stat-simple-label">Avaliações 4-5</span>
                </div>
            </div>

            <div className="precision-highlight">
                <div className="precision-circle">
                    <svg width="132" height="132" viewBox="0 0 132 132">
                        <circle cx="66" cy="66" r="60" fill="none"
                            stroke={svgTrack} strokeWidth="4" />
                        <circle cx="66" cy="66" r="60" fill="none"
                            stroke={svgFill} strokeWidth="4"
                            strokeDasharray={`${(precision / 100) * 376.99} 376.99`}
                            strokeLinecap="round"
                            transform="rotate(-90 66 66)" />
                    </svg>
                    <div className="precision-number">{precision}%</div>
                </div>
                <span className="precision-label">Precisão da sessão</span>
            </div>

            <div className="ratings-section-quiet">
                <h4 className="section-title-quiet">Distribuição</h4>
                <div className="rating-distribution-quiet">
                    {[1, 2, 3, 4, 5].map(level => {
                        const count = ratings[level as keyof typeof ratings];
                        const percentage = cardsStudied > 0 ? (count / cardsStudied) * 100 : 0;
                        return (
                            <div key={level} className="rating-row-quiet">
                                <span className={`rating-label-quiet level-${level}`}>{level}</span>
                                <div className="rating-bar-wrapper-quiet">
                                    <div className={`rating-bar-quiet level-${level}`}
                                        style={{ width: `${percentage}%` }} />
                                </div>
                                <span className="rating-count-quiet">{count}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {cardsStudied > 0 && (
                <div className="motivational-whisper">{getMotivationalMessage()}</div>
            )}
        </div>
    );
};

// =====================================================================
// ABA: PROGRESSO
// =====================================================================
const ProgressTab: React.FC<{
    data: SubdeckProgressData | null;
    loading: boolean;
}> = ({ data, loading }) => {
    const colorMastered = getComputedStyle(document.body).getPropertyValue('--platform-accent').trim();
    const colorReviewing = getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim();

    if (loading) {
        return (
            <div className="sidebar-content">
                <div className="sdp-progress-loading">Carregando...</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="sidebar-content">
                <div className="sdp-progress-empty">
                    Nenhum dado de progresso disponível para este subdeck.
                </div>
            </div>
        );
    }

    const coveragePercent = data.totalCards > 0
        ? Math.round((data.uniqueCardsStudied / data.totalCards) * 100)
        : 0;

    return (
        <div className="sidebar-content sidebar-content--progress">

            {/* 1. DOMÍNIO ACUMULADO */}
            <div className="prog-block">
                <div className="prog-mastery-compact">
                    <span className="prog-mastery-value-sm">{data.masteryPercentage}%</span>
                    <div className="prog-mastery-right">
                        <span className="prog-mastery-label">domínio acumulado</span>
                        {data.masteryDelta !== 0 && (
                            <span className={`prog-delta-inline ${data.masteryDelta > 0 ? 'prog-delta--up' : 'prog-delta--down'}`}>
                                {formatDelta(data.masteryDelta)} desde a última sessão
                            </span>
                        )}
                        {data.masteryDelta === 0 && data.lastSession && (
                            <span className="prog-delta-inline prog-delta--neutral">estável desde a última sessão</span>
                        )}
                    </div>
                </div>
                <div className="prog-bar-bg">
                    <div className="prog-bar-fill" style={{ width: `${data.masteryPercentage}%` }} />
                </div>
            </div>

            {/* 2. COBERTURA */}
            <div className="prog-block">
                <div className="prog-coverage-compact">
                    <span className="prog-coverage-nums-sm">
                        <strong>{data.uniqueCardsStudied}</strong> / {data.totalCards}
                    </span>
                    <span className="prog-coverage-label">cards únicos estudados · {coveragePercent}% do subdeck</span>
                </div>
                <div className="prog-bar-bg prog-bar-bg--slim">
                    <div className="prog-bar-fill prog-bar-fill--neutral" style={{ width: `${coveragePercent}%` }} />
                </div>
            </div>

            {/* 3. DISTRIBUIÇÃO GLOBAL */}
            <div className="prog-block">
                <span className="prog-block-title">Distribuição acumulada</span>
                <div className="prog-dist-grid">
                    {[
                        { key: 'mastered', label: 'Dominados', color: colorMastered },
                        { key: 'reviewing', label: 'Revisando', color: colorReviewing },
                        { key: 'learning', label: 'Aprendendo', color: '#ca8a04' },
                        { key: 'new', label: 'Não vistos', color: '#9CA3AF' },
                    ].map(({ key, label, color }) => {
                        const count = data.statusDistribution[key as keyof typeof data.statusDistribution] || 0;
                        const pct = data.totalCards > 0 ? (count / data.totalCards) * 100 : 0;
                        const barWidth = count > 0 ? Math.max(pct, 3) : 0;
                        return (
                            <div key={key} className="prog-dist-row">
                                <div className="prog-dist-label-group">
                                    <span className="prog-dist-dot" style={{ background: color }} />
                                    <span className="prog-dist-label">{label}</span>
                                </div>
                                <div className="prog-dist-bar-bg">
                                    <div className="prog-dist-bar-fill"
                                        style={{ width: `${barWidth}%`, background: color }} />
                                </div>
                                <span className="prog-dist-count">{count}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 4+5. ÚLTIMA SESSÃO */}
            {data.lastSession ? (
                <div className="prog-block prog-last-section">
                    <span className="prog-block-title">Última sessão · {formatTimestamp(data.lastSession.timestamp)}</span>
                    <div className="prog-last-cards-row">
                        <div className="prog-last-card">
                            <span className="prog-last-card-value">{data.lastSession.cardsStudied}</span>
                            <span className="prog-last-card-label">cards</span>
                        </div>
                        <div className="prog-last-card">
                            <span className="prog-last-card-value">{data.lastSession.accuracy}%</span>
                            <span className="prog-last-card-label">precisão</span>
                        </div>
                    </div>
                    {data.accuracyDelta !== null && data.accuracyDelta !== 0 && (
                        <div className={`prog-comparison-pill ${data.accuracyDelta > 0 ? 'prog-comparison--up' : 'prog-comparison--down'}`}>
                            {data.accuracyDelta > 0 ? `+${data.accuracyDelta}%` : `${data.accuracyDelta}%`} precisão vs anterior
                        </div>
                    )}
                    {data.accuracyDelta === 0 && (
                        <div className="prog-comparison-pill prog-comparison--neutral">Precisão estável</div>
                    )}
                </div>
            ) : (
                <div className="prog-no-history">Primeira sessão neste subdeck.</div>
            )}
        </div>
    );
};

// =====================================================================
// COMPONENTE PRINCIPAL
// =====================================================================
export const StudySidebarPanel: React.FC<StudySidebarPanelProps> = ({
    isOpen,
    onClose,
    cardsStudied,
    correctAnswers,
    precision,
    ratings,
    progressData,
    progressLoading,
    onOpenProgressTab,
}) => {
    const [activeTab, setActiveTab] = useState<'session' | 'progress'>('session');
    // ✅ CACHE: controla se já buscou dados para não recarregar ao trocar abas
    const [progressFetched, setProgressFetched] = useState(false);
    const showProgressTab = progressData !== undefined;

    const handleTabChange = (tab: 'session' | 'progress') => {
        setActiveTab(tab);
        // Só busca se ainda não buscou nesta abertura da sidebar
        if (tab === 'progress' && !progressFetched && onOpenProgressTab) {
            onOpenProgressTab();
            setProgressFetched(true);
        }
    };

    // Resetar ao fechar — próxima abertura começa limpa
    useEffect(() => {
        if (!isOpen) {
            setTimeout(() => {
                setActiveTab('session');
                setProgressFetched(false);
            }, 300);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <div className="sidebar-overlay" onClick={onClose} />
            <div className="sidebar-panel">

                {/* ✅ Header compacto: abas + botão fechar na mesma linha */}
                <div className="sidebar-header-new">
                    {showProgressTab ? (
                        <div className="sidebar-tabs-inline">
                            <button
                                className={`sidebar-tab-btn ${activeTab === 'session' ? 'sidebar-tab-btn--active' : ''}`}
                                onClick={() => handleTabChange('session')}
                            >
                                Sessão atual
                            </button>
                            <button
                                className={`sidebar-tab-btn ${activeTab === 'progress' ? 'sidebar-tab-btn--active' : ''}`}
                                onClick={() => handleTabChange('progress')}
                            >
                                Progresso
                            </button>
                        </div>
                    ) : (
                        <span className="sidebar-title-plain">Sessão Atual</span>
                    )}
                    <button className="btn-close-sidebar-new" onClick={onClose} aria-label="Fechar painel">
                        <X size={14} />
                    </button>
                </div>

                {/* Conteúdo */}
                {activeTab === 'session' ? (
                    <SessionTab
                        cardsStudied={cardsStudied}
                        correctAnswers={correctAnswers}
                        precision={precision}
                        ratings={ratings}
                    />
                ) : (
                    <ProgressTab
                        data={progressData || null}
                        loading={progressLoading || false}
                    />
                )}
            </div>
        </>
    );
};